import pako from 'pako';
import type { BootData } from '@/types/boot';
import { getBootPublicKey } from './getBootPublicKey';

function cleanPublicKey(publicKey: string): string {
  return publicKey
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
}

function base64UrlToBase64(value: string): string {
  return value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64UrlToUint8Array(value: string): Uint8Array {
  return base64ToUint8Array(base64UrlToBase64(value));
}

function base64UrlToString(value: string): string {
  return new TextDecoder().decode(base64UrlToUint8Array(value));
}

async function getPublicKey(): Promise<CryptoKey> {
  // const publicKeyPem = getEnvVariable('VITE_BOOT_JWT_PUBLIC_KEY') || DEFAULT_BOOT_JWT_PUBLIC_KEY;
  const publicKeyPem = getBootPublicKey();
  const cleaned = cleanPublicKey(publicKeyPem);
  const spkiBytes = base64ToUint8Array(cleaned);
  const spkiDer = spkiBytes.buffer.slice(
    spkiBytes.byteOffset,
    spkiBytes.byteOffset + spkiBytes.byteLength
  ) as ArrayBuffer;

  return crypto.subtle.importKey('spki', spkiDer, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['verify']);
}

function decodeCompressedPayload(d: string): Record<string, unknown> {
  const compressed = base64UrlToUint8Array(d);
  const decompressed = pako.inflate(compressed);
  const json = new TextDecoder().decode(decompressed);
  return JSON.parse(json) as Record<string, unknown>;
}

async function verifyAndDecodeJWT(jwt: string): Promise<Record<string, unknown>> {
  const [headerB64, payloadB64, signatureB64] = jwt.split('.');

  if (!headerB64 || !payloadB64 || !signatureB64) {
    throw new Error('Malformed JWT');
  }

  const header = JSON.parse(base64UrlToString(headerB64)) as { alg?: string };

  if (header.alg !== 'ES256') {
    throw new Error(`Unsupported alg: ${header.alg}`);
  }

  const publicKey = await getPublicKey();
  const dataToVerify = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
  const signature = base64UrlToUint8Array(signatureB64);

  const isValid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    signature as BufferSource,
    dataToVerify as BufferSource
  );

  if (!isValid) {
    throw new Error('Invalid signature');
  }

  const payload = JSON.parse(base64UrlToString(payloadB64)) as { d?: string };

  if (!payload.d) {
    throw new Error('Payload does not contain d');
  }

  return decodeCompressedPayload(payload.d);
}

export const getBootFromUrlHash = async (): Promise<BootData> => {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) {
    throw new Error('Boot data not found in URL hash');
  }

  const hashContent = hash.substring(1);
  const hashParams = new URLSearchParams(hashContent);
  const bootValue = hashParams.get('boot');

  if (!bootValue) {
    throw new Error('Boot data not found in URL hash');
  }

  if (!bootValue.includes('.')) {
    // TODO: remove - legacy support for unsigned base64 boot
    try {
      const decoded = atob(bootValue);
      return JSON.parse(decoded) as BootData;
    } catch {
      throw new Error('Invalid boot format: expected signed JWT');
    }
  }

  return (await verifyAndDecodeJWT(bootValue)) as unknown as BootData;
};

export const getBoot = async (): Promise<BootData> => {
  return getBootFromUrlHash();
};
